"use strict";
import { CurrencyAmount, Price } from "@uniswap/sdk-core";
import { tickToPrice } from "@uniswap/v3-sdk";
import { ethers } from "ethers";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { getBscContracts, getBscSubgraphs, isBscChain } from "constants/bscContracts";
import { USDC_BSC_MAINNET, USDC_BSC_TESTNET, getStablecoins, getToken, nativeOnChain } from "constants/topazTokenList";
import { RPCType, UniverseChainId, isUniverseChainId } from "features/chains/types";
import { getChainInfo } from "features/chains/chainInfo";
import { areCurrencyIdsEqual, currencyId } from "utils/uniswapCurrencyId";
import { useEnabledChains } from "features/chains/hooks/useEnabledChains";
import { wrappedNativeCurrency } from "utils/currency";
import { fetchFromSubgraph } from "utils/fetchSubgraph";
const USDC_DEFAULT_MIN = 1e9;
const USDC_18_DEFAULT_MIN = 1e21;
export const STABLECOIN_AMOUNT_OUT = {
  [UniverseChainId.Mainnet]: CurrencyAmount.fromRawAmount(USDC_BSC_MAINNET, USDC_DEFAULT_MIN),
  [UniverseChainId.Sepolia]: CurrencyAmount.fromRawAmount(USDC_BSC_TESTNET, USDC_18_DEFAULT_MIN)
};
function scaleUsdToInteger(price, decimals) {
  const priceStr = price.toString();
  if (priceStr.includes("e")) {
    return "0";
  }
  const [whole, fractional = ""] = priceStr.split(".");
  const paddedFractional = fractional.padEnd(decimals, "0").slice(0, decimals);
  const combined = (whole + paddedFractional).replace(/^0+(?=\d)/, "");
  return combined || "0";
}
function getTestnetHardcodedPrice(tokenAddress) {
  const lower = tokenAddress.toLowerCase();
  const contracts = getBscContracts(UniverseChainId.Sepolia);
  if (lower === contracts.wbnb.toLowerCase()) return "600";
  if (lower === contracts.topaz.toLowerCase()) return "0.20";
  return void 0;
}
const V3_TICK_SPACINGS = [1, 50, 100, 200, 2e3];
export function normalizeCurrencyForUsdPricing(currency) {
  if (!currency || !isUniverseChainId(currency.chainId)) {
    return currency;
  }
  if (currency.isToken) {
    const nativeCurrency = nativeOnChain(currency.chainId);
    const wrapped = wrappedNativeCurrency(currency.chainId);
    const hasEquals = typeof currency?.equals === "function";
    const matchesWrapped = hasEquals ? currency.equals(wrapped) : typeof currency?.address === "string" && currency.address.toLowerCase() === wrapped.address.toLowerCase();
    if (matchesWrapped) {
      return nativeCurrency;
    }
  }
  return currency;
}
async function fetchTokenPriceFromSubgraph(chainId, tokenAddress) {
  if (!isBscChain(chainId)) return null;
  const subgraphs = getBscSubgraphs(chainId);
  const lower = tokenAddress.toLowerCase();
  try {
    const v3 = await fetchFromSubgraph(subgraphs.v3, {
      query: `query TokenPrice($id: ID!) {
        bundle(id: "1") { ethPriceUSD }
        token(id: $id) {
          derivedETH
          tokenDayData(first: 1, orderBy: date, orderDirection: desc) { priceUSD }
        }
      }`,
      variables: { id: lower },
      requestId: "useUSDCPrice.subgraph.v3"
    });
    const dayPrice = parseFloat(v3?.token?.tokenDayData?.[0]?.priceUSD ?? "0");
    if (Number.isFinite(dayPrice) && dayPrice > 0) return dayPrice;
    const derivedEth = parseFloat(v3?.token?.derivedETH ?? "0");
    const ethUsd = parseFloat(v3?.bundle?.ethPriceUSD ?? "0");
    if (derivedEth > 0 && ethUsd > 0) {
      const price = derivedEth * ethUsd;
      if (Number.isFinite(price) && price > 0) return price;
    }
  } catch (error) {
    console.warn("[useUSDCPrice] V3 subgraph price lookup failed:", error);
  }
  try {
    const v2 = await fetchFromSubgraph(subgraphs.v2, {
      query: `query TokenPriceV2($id: ID!) {
        bundle(id: "1") { ethPrice }
        token(id: $id) { derivedETH }
      }`,
      variables: { id: lower },
      requestId: "useUSDCPrice.subgraph.v2"
    });
    const derivedEth = parseFloat(v2?.token?.derivedETH ?? "0");
    const ethUsd = parseFloat(v2?.bundle?.ethPrice ?? "0");
    if (derivedEth > 0 && ethUsd > 0) {
      const price = derivedEth * ethUsd;
      if (Number.isFinite(price) && price > 0) return price;
    }
  } catch (error) {
    console.warn("[useUSDCPrice] V2 subgraph price lookup failed:", error);
  }
  return null;
}
async function fetchPoolPriceFromBscDex(chainId, baseCurrency, usdcToken) {
  if (!isBscChain(chainId)) {
    return null;
  }
  const { clFactory } = getBscContracts(chainId);
  if (!clFactory) {
    return null;
  }
  const rpcUrl = getChainInfo(chainId).rpcUrls[RPCType.Default]?.http?.[0];
  if (!rpcUrl) {
    return null;
  }
  const provider = new ethers.providers.JsonRpcProvider(rpcUrl);
  const baseToken = baseCurrency.isToken ? baseCurrency : baseCurrency.wrapped;
  const quoteToken = usdcToken;
  const baseSortsBefore = baseToken.sortsBefore(quoteToken);
  const token0 = baseSortsBefore ? baseToken : quoteToken;
  const token1 = baseSortsBefore ? quoteToken : baseToken;
  try {
    const factoryAbi = ["function getPool(address tokenA, address tokenB, int24 tickSpacing) view returns (address pool)"];
    const clFactoryContract = new ethers.Contract(clFactory, factoryAbi, provider);
    const poolAddressPromises = V3_TICK_SPACINGS.map(
      (tickSpacing) => clFactoryContract.getPool(token0.address, token1.address, tickSpacing)
    );
    const poolAddresses = await Promise.all(poolAddressPromises);
    const poolAbi = [
      "function slot0() view returns (uint160 sqrtPriceX96, int24 tick, uint16 observationIndex, uint16 observationCardinality, uint16 observationCardinalityNext, uint8 feeProtocol)"
    ];
    const existingPools = [];
    poolAddresses.forEach((address, index) => {
      if (address !== "0x0000000000000000000000000000000000000000") {
        const tickSpacing = V3_TICK_SPACINGS[index];
        if (tickSpacing !== void 0) {
          existingPools.push({ address, tickSpacing, index });
        }
      }
    });
    if (existingPools.length === 0) {
      return null;
    }
    const slot0Promises = existingPools.map((pool) => {
      const poolContract = new ethers.Contract(pool.address, poolAbi, provider);
      return poolContract.slot0().catch(() => null);
    });
    const slot0Results = await Promise.all(slot0Promises);
    for (let i = 0; i < existingPools.length; i++) {
      const slot0 = slot0Results[i];
      if (!slot0 || slot0.sqrtPriceX96.toString() === "0") {
        continue;
      }
      const tick = slot0.tick;
      const priceForToken0 = tickToPrice(token0, token1, tick);
      const priceForToken1 = tickToPrice(token1, token0, tick);
      const priceForBase = baseSortsBefore ? priceForToken0 : priceForToken1;
      return new Price(baseCurrency, usdcToken, priceForBase.denominator, priceForBase.numerator);
    }
    return null;
  } catch (error) {
    console.error("Failed to fetch pool price from Topaz:", error);
    return null;
  }
}
function mapPriceToRequestedCurrency(price, requestedCurrency, normalizedCurrency) {
  if (!price) {
    return void 0;
  }
  if (!requestedCurrency || !normalizedCurrency) {
    return price ?? void 0;
  }
  const requestedHasEquals = typeof requestedCurrency?.equals === "function";
  if (requestedCurrency.isNative && normalizedCurrency.isNative && requestedCurrency.chainId === normalizedCurrency.chainId) {
    return price;
  }
  if (requestedHasEquals && requestedCurrency.equals(normalizedCurrency)) {
    return price;
  }
  if (normalizedCurrency.isNative && isUniverseChainId(normalizedCurrency.chainId)) {
    const wrapped = wrappedNativeCurrency(normalizedCurrency.chainId);
    const matchesWrapped = requestedHasEquals ? requestedCurrency.equals(wrapped) : requestedCurrency.isToken && typeof requestedCurrency?.address === "string" && requestedCurrency.address.toLowerCase() === wrapped.address.toLowerCase();
    if (matchesWrapped) {
      return new Price(wrapped, price.quoteCurrency, price.denominator, price.numerator);
    }
  }
  return price;
}
export function useUSDCPrice(currency) {
  const normalizedCurrency = useMemo(() => normalizeCurrencyForUsdPricing(currency), [currency]);
  const chainId = normalizedCurrency?.chainId;
  const { isTestnetModeEnabled } = useEnabledChains();
  const chainMatchesMode = useMemo(() => {
    if (!isUniverseChainId(chainId)) return false;
    return isTestnetModeEnabled ? chainId === UniverseChainId.Sepolia : chainId === UniverseChainId.Mainnet;
  }, [chainId, isTestnetModeEnabled]);
  const usdcToken = chainMatchesMode && isUniverseChainId(chainId) ? getToken(chainId).usdc : void 0;
  const stablecoins = useMemo(
    () => chainMatchesMode && isUniverseChainId(chainId) ? getStablecoins(chainId) : [],
    [chainId, chainMatchesMode]
  );
  const currencyIsStablecoin = Boolean(
    normalizedCurrency && stablecoins.some((stable) => areCurrencyIdsEqual(currencyId(normalizedCurrency), currencyId(stable)))
  );
  const { data, isLoading } = useQuery({
    queryKey: ["token-usd-price", chainId, normalizedCurrency?.wrapped.address],
    enabled: Boolean(
      normalizedCurrency && usdcToken && !currencyIsStablecoin && isUniverseChainId(chainId) && chainMatchesMode
    ),
    refetchInterval: 6e4,
    // Refetch every 60 seconds
    staleTime: 6e4,
    // Consider data fresh for 60 seconds - React Query won't refetch during this time
    gcTime: 3e5,
    // Keep unused data in cache for 5 minutes (was cacheTime in v4)
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    // Don't refetch on mount if data is in cache and not stale
    queryFn: async () => {
      if (!normalizedCurrency || !usdcToken || !isUniverseChainId(chainId) || !chainMatchesMode) {
        return null;
      }
      const tokenAddr = normalizedCurrency.isNative ? normalizedCurrency.wrapped.address : normalizedCurrency.address;
      if (!tokenAddr || tokenAddr.toLowerCase() === "0x0000000000000000000000000000000000000000") {
        return null;
      }
      if (chainId === UniverseChainId.Sepolia) {
        const hardcodedPrice = getTestnetHardcodedPrice(tokenAddr);
        if (hardcodedPrice) {
          const tokenDecimals = normalizedCurrency.decimals;
          const usdcDecimals = usdcToken.decimals;
          const denominator = scaleUsdToInteger(1, tokenDecimals);
          const numerator = scaleUsdToInteger(parseFloat(hardcodedPrice), usdcDecimals);
          return new Price(normalizedCurrency, usdcToken, denominator, numerator);
        }
        return null;
      }
      let priceUsdNumber = null;
      try {
        const response = await fetch(
          `https://api.dexscreener.com/tokens/v1/bsc/${tokenAddr}`,
          { headers: { "Accept": "application/json" } }
        );
        if (!response.ok) {
          throw new Error(`DexScreener request failed with status ${response.status}`);
        }
        const pairs = await response.json();
        if (pairs && pairs.length > 0) {
          const normalizedAddress = tokenAddr.toLowerCase();
          const candidate = pairs.filter((p) => p.priceUsd && p.liquidity?.usd).sort((a, b) => (b.liquidity?.usd || 0) - (a.liquidity?.usd || 0))[0];
          if (candidate?.priceUsd) {
            const isBaseToken = candidate.baseToken.address.toLowerCase() === normalizedAddress;
            const basePrice = parseFloat(candidate.priceUsd);
            let parsed = basePrice;
            if (!isBaseToken) {
              const priceNative = parseFloat(candidate.priceNative ?? "0");
              parsed = priceNative > 0 ? basePrice / priceNative : 0;
            }
            if (Number.isFinite(parsed) && parsed > 0) {
              priceUsdNumber = parsed;
            }
          }
        }
      } catch (error) {
        console.warn("[useUSDCPrice] DexScreener fetch failed, falling back to subgraph:", error);
      }
      if (!priceUsdNumber) {
        try {
          const subgraphPrice = await fetchTokenPriceFromSubgraph(chainId, tokenAddr);
          if (subgraphPrice && Number.isFinite(subgraphPrice) && subgraphPrice > 0) {
            priceUsdNumber = subgraphPrice;
          }
        } catch (error) {
          console.warn("[useUSDCPrice] Subgraph price lookup failed:", error);
        }
      }
      if (priceUsdNumber && Number.isFinite(priceUsdNumber) && priceUsdNumber > 0) {
        const tokenDecimals = normalizedCurrency.decimals;
        const usdcDecimals = usdcToken.decimals;
        const denominator = scaleUsdToInteger(1, tokenDecimals);
        const numerator = scaleUsdToInteger(priceUsdNumber, usdcDecimals);
        return new Price(normalizedCurrency, usdcToken, denominator, numerator);
      }
      return isUniverseChainId(chainId) && usdcToken ? await fetchPoolPriceFromBscDex(chainId, normalizedCurrency, usdcToken) : null;
    }
  });
  return useMemo(() => {
    const mappedPrice = mapPriceToRequestedCurrency(data ?? void 0, currency, normalizedCurrency);
    if (!usdcToken || !chainMatchesMode) {
      return { price: void 0, isLoading: false };
    }
    if (currencyIsStablecoin && normalizedCurrency) {
      const decimals = Math.pow(10, normalizedCurrency.decimals).toString();
      const usdcDecimals = Math.pow(10, usdcToken.decimals).toString();
      return {
        price: new Price(normalizedCurrency, usdcToken, decimals, usdcDecimals),
        isLoading: false
      };
    }
    return { price: mappedPrice, isLoading };
  }, [chainMatchesMode, currency, currencyIsStablecoin, data, isLoading, normalizedCurrency, usdcToken]);
}
export function useUSDCValue(currencyAmount) {
  const { price } = useUSDCPrice(currencyAmount?.currency);
  return useMemo(() => {
    if (!price || !currencyAmount) {
      return null;
    }
    try {
      return price.quote(currencyAmount);
    } catch (error) {
      return null;
    }
  }, [currencyAmount, price]);
}
export function useUSDCValueWithStatus(currencyAmount) {
  const { price, isLoading } = useUSDCPrice(currencyAmount?.currency);
  return useMemo(() => {
    if (!price || !currencyAmount) {
      return { value: null, isLoading };
    }
    try {
      return { value: price.quote(currencyAmount), isLoading };
    } catch (error) {
      return {
        value: null,
        isLoading: false
      };
    }
  }, [currencyAmount, isLoading, price]);
}
